import React from 'react';
import Icon from '../../../components/AppIcon';

const RegistrationProgress = ({ currentStep = 1, totalSteps = 3 }) => {
  const steps = [
    {
      id: 1,
      title: 'Account Details',
      description: 'Basic information and account type',
      icon: 'User'
    },
    {
      id: 2,
      title: 'Verification',
      description: 'Email verification and phone confirmation',
      icon: 'Mail'
    },
    {
      id: 3,
      title: 'Welcome',
      description: 'Account setup complete',
      icon: 'CheckCircle'
    }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (step) => {
    const status = getStepStatus(step?.id);
    if (status === 'completed') return 'CheckCircle';
    if (status === 'current') return step?.icon;
    return step?.icon;
  };

  const getStepIconColor = (stepId) => {
    const status = getStepStatus(stepId);
    if (status === 'completed') return 'text-success';
    if (status === 'current') return 'text-primary';
    return 'text-muted-foreground';
  };

  const getStepBgColor = (stepId) => {
    const status = getStepStatus(stepId);
    if (status === 'completed') return 'bg-success';
    if (status === 'current') return 'bg-primary';
    return 'bg-muted';
  };

  const getConnectorColor = (stepId) => {
    return stepId < currentStep ? 'bg-success' : 'bg-muted';
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-border">
      <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center">
        <Icon name="Route" size={20} className="mr-2 text-primary" />
        Registration Progress
      </h3>
      <div className="relative">
        {steps?.map((step, index) => (
          <div key={step?.id} className="relative flex items-start">
            {/* Step Icon */}
            <div className="relative flex-shrink-0">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getStepBgColor(step?.id)}`}>
                <Icon 
                  name={getStepIcon(step)} 
                  size={18} 
                  className={getStepStatus(step?.id) === 'completed' ? 'text-white' : 
                           getStepStatus(step?.id) === 'current' ? 'text-white' : 'text-muted-foreground'} 
                />
              </div>
              
              {/* Connector Line */}
              {index < steps?.length - 1 && (
                <div className={`absolute top-10 left-5 w-0.5 h-12 -ml-px ${getConnectorColor(step?.id)}`} />
              )}
            </div>

            {/* Step Content */}
            <div className="ml-4 pb-8">
              <h4 className={`text-sm font-medium ${
                getStepStatus(step?.id) === 'current' ? 'text-primary' : 
                getStepStatus(step?.id) === 'completed' ? 'text-success' : 'text-muted-foreground'
              }`}>
                {step?.title}
                {getStepStatus(step?.id) === 'current' && (
                  <span className="ml-2 text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    Current
                  </span>
                )}
                {getStepStatus(step?.id) === 'completed' && (
                  <span className="ml-2 text-xs bg-success/10 text-success px-2 py-0.5 rounded-full">
                    Complete
                  </span>
                )}
              </h4>
              <p className="text-xs text-muted-foreground mt-1">{step?.description}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Progress Bar */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Progress</span>
          <span className="text-foreground font-medium">
            {Math.round(((currentStep - 1) / (totalSteps - 1)) * 100)}%
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
          />
        </div>
      </div>
      {/* Next Steps Info */}
      <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
        <h4 className="text-sm font-medium text-foreground mb-2 flex items-center">
          <Icon name="Info" size={16} className="mr-2 text-primary" />
          What happens next?
        </h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li className="flex items-center">
            <Icon name="ArrowRight" size={12} className="mr-2 text-primary" />
            Email verification link will be sent to your inbox
          </li>
          <li className="flex items-center">
            <Icon name="ArrowRight" size={12} className="mr-2 text-primary" />
            SMS confirmation code for phone verification
          </li>
          <li className="flex items-center">
            <Icon name="ArrowRight" size={12} className="mr-2 text-primary" />
            Access to your personalized dashboard
          </li>
          <li className="flex items-center">
            <Icon name="ArrowRight" size={12} className="mr-2 text-primary" />
            Start browsing our ICT solutions catalog
          </li>
        </ul>
      </div>
    </div>
  );
};

export default RegistrationProgress;